<template>
  <div
    ref="root"
    class="flex flex-col p-2.5 pl-2.5 pr-2 items-start gap-2 self-stretch rounded-xl bg-fill-brand-secondary mt-2"
  >
    <div
      class="flex p-0 pr-[9px] pb-2.5 items-start gap-2 self-stretch border-b border-border"
    >
      <Icon
        icon="mdi:question-box-multiple"
        class="w-5 h-5 text-lg text-basics-primary"
      />
      <span
        class="flex-1 font-sans text-sm font-medium text-text select-none"
        >{{ $t("channel.chat.askHostQuestion") }}</span
      >
      <button
        class="flex w-5 h-5 !min-w-5 !min-h-5 justify-center items-center gap-2 rounded-full"
        aria-label="Filter questions"
        @click="emit('closeQuestions')"
      >
        <Icon
          icon="mdi:close"
          class="w-5 h-5 text-lg text-basics-primary"
          aria-hidden
        />
      </button>
    </div>

    <form
      id="chat-question-form"
      ref="chatForm"
      class="sticky h-10 w-full bottom-0 z-10 bg-brand-surface rounded-3xl pr-0.5 py-1 focus-within:border-brand-primary gap-2.5 transition-all flex flex-col justify-center"
      @click.stop
      @submit.prevent="sendMessage"
    >
      <div class="flex flex-row items-center gap-1">
        <textarea
          id="chat-question-input"
          ref="inputRef"
          v-model="input"
          type="text"
          name="message"
          :aria-label="$t('channel.chat.askHostQuestion')"
          :placeholder="$t('channel.chat.typeHere')"
          :maxlength="300"
          :rows="1"
          required
          class="w-full placeholder:text-text-tertiary text-sm p-0 text-white bg-transparent border-none focus:outline-none resize-none border user-invalid:border-basics-accent-red overflow-x-auto max-h-20 font-medium font-sans whitespace-nowrap hide-scrollbar"
          @focus="onTextInputFocus"
          @blur="() => emit('setKeyboardOpen', false)"
          @keydown="onEnterPress"
        ></textarea>

        <ButtonV2
          variant="square"
          size="sm"
          type="submit"
          :disabled="input.length === 0"
          aria-label="Send message"
          @click="triggerFormSubmit"
        >
          <Icon
            icon="material-symbols-light:arrow-upward-alt-rounded"
            class="w-5 h-5 text-lg text-basics-primary"
            aria-hidden
          />
        </ButtonV2>
      </div>
    </form>
  </div>
</template>

<script setup lang="ts">
import { watch, useTemplateRef } from "vue";
import { Icon } from "@iconify/vue";
import { useTextareaAutosize } from "@vueuse/core";
import { useShowStore } from "@/stores/show.store";
import { useChannelStore } from "@/stores/channel.store";
import ButtonV2 from "../ui/button/button-v2.vue";
import { useI18n } from "vue-i18n";

const MAX_FIELD_LINES = 5;

const { t } = useI18n();
const _showStore = useShowStore();
const _channelStore = useChannelStore();

const chatForm = useTemplateRef("chatForm");

const message = defineModel<string>({
  required: true,
});

const emit = defineEmits<{
  submit: [];
  clearReply: [];
  goToUpstream: [messageId: string];
  setExpanded: [expanded: boolean];
  setKeyboardOpen: [isOpen: boolean];
  closeQuestions: [];
}>();

const _props = defineProps<{
  keyboardOpen: boolean;
}>();

const root = useTemplateRef("root");
defineExpose({
  root,
});

const { textarea: inputRef, input } = useTextareaAutosize({
  input: message,
});

watch(message, () => {
  const lines = message.value.split("\n").length;
  if (lines > MAX_FIELD_LINES) {
    inputRef.value?.setCustomValidity(
      t("channel.chat.tooManyLines", { lines }),
    );
    inputRef.value?.reportValidity();
  } else {
    inputRef.value?.setCustomValidity("");
  }
});

function sendMessage() {
  inputRef.value?.focus(); // Set focus back to the textarea
  emit("submit");
}

const onEnterPress = (e: KeyboardEvent) => {
  if (e.key === "Enter" && !e.shiftKey) {
    // Prevent form auto-submit on Enter
    e.preventDefault();

    // Validate if the input is valid
    const isValid = (e.target as HTMLTextAreaElement).checkValidity();

    // If valid, attempt to send the message
    if (isValid) {
      sendMessage();
      clearReply();
    }
  } else if (e.key === "Escape") {
    clearReply();
  }
};

function clearReply() {
  emit("clearReply");
}

function triggerFormSubmit() {
  if (chatForm.value) {
    chatForm.value.dispatchEvent(new Event("submit", { cancelable: true }));
    emit("setExpanded", false);
  }
}

function onTextInputFocus() {
  setTimeout(() => {
    emit("setKeyboardOpen", true);
    emit("setExpanded", true);
  }, 100);
}
</script>

<style lang="css" scoped>
@supports (-webkit-overflow-scrolling: touch) {
  textarea {
    font-size: 16px;
  }
}
</style>
